import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useMyAccountActions from '../../hooks/myAccountActions-hook';
import { useAppSelector } from '../../hooks/store-hook';
import Overlay from '../common/Overlay';
import MyAccountNavigator from './MyAccountNavigator';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import NavigationElements from '../../models/NavigationElements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  Easing,
  cancelAnimation,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const MyAccount = () => {
  const myAccountStore = useAppSelector((selector) => selector.myAccount);
  const myAccountActions = useMyAccountActions();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const [mounted, setMounted] = useState(false);
  const progress = useSharedValue(0);
  const panelWidth = Platform.OS === 'web' ? Math.min(375, layout.width) : layout.width;

  const OPEN_DURATION_MS = 240;
  const CLOSE_DURATION_MS = 190;
  const SNAP_BACK_DURATION_MS = 180;
  const SWIPE_CLOSE_DURATION_MS = 180;

  useEffect(() => {
    if (myAccountStore.visible) {
      setMounted(true);
      progress.value = 0;
      progress.value = withTiming(1, { duration: OPEN_DURATION_MS, easing: Easing.out(Easing.cubic) });
      return;
    }

    if (!mounted) return;
    // Keep the overlay mounted while sliding out, then unmount.
    progress.value = withTiming(0, { duration: CLOSE_DURATION_MS, easing: Easing.in(Easing.cubic) }, (finished) => {
      if (finished) runOnJS(setMounted)(false);
    });
  }, [myAccountStore.visible, mounted, progress, OPEN_DURATION_MS, CLOSE_DURATION_MS]);

  const panelStyle = useAnimatedStyle(() => {
    const translateX = interpolate(progress.value, [0, 1], [panelWidth, 0]);
    return {
      transform: [{ translateX }],
    };
  }, [panelWidth]);

  const closeMyAccount = useCallback(() => {
    myAccountActions.setVisibility(false);
  }, [myAccountActions]);

  const canSwipeToDismiss = Platform.OS !== 'web' && mounted && myAccountStore.visible && myAccountStore.currentRoute === NavigationElements.menu;

  const swipeToDismissGesture = useMemo(() => {
    return Gesture.Pan()
      .enabled(canSwipeToDismiss)
      // Avoid fighting vertical ScrollView gestures.
      .activeOffsetX([-10, 10])
      .failOffsetY([-12, 12])
      .onBegin(() => {
        cancelAnimation(progress);
      })
      .onUpdate((e) => {
        const dx = Math.max(0, e.translationX);
        const nextProgress = 1 - Math.min(dx, layout.width) / layout.width;
        progress.value = nextProgress;
      })
      .onEnd((e) => {
        const shouldClose = e.translationX > layout.width * 0.18 || e.velocityX > 750;
        if (shouldClose) {
          progress.value = withTiming(0, { duration: SWIPE_CLOSE_DURATION_MS, easing: Easing.out(Easing.cubic) }, (finished) => {
            if (!finished) return;
            runOnJS(closeMyAccount)();
            runOnJS(setMounted)(false);
          });
        } else {
          progress.value = withTiming(1, { duration: SNAP_BACK_DURATION_MS, easing: Easing.out(Easing.cubic) });
        }
      });
  }, [canSwipeToDismiss, closeMyAccount, layout.width, progress, SNAP_BACK_DURATION_MS, SWIPE_CLOSE_DURATION_MS]);

  return (
    <>
      {mounted ? (
        <>
          <Overlay showOverLay={mounted} zIndex={4001} onPress={closeMyAccount} />
          <GestureDetector gesture={swipeToDismissGesture}>
            <Animated.View
              style={[
                Platform.OS === 'web'
                  ? {
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: panelWidth,
                    }
                  : { ...StyleSheet.absoluteFillObject, top: -insets.top },
                { zIndex: 4002 },
                panelStyle,
              ]}
            >
              <MyAccountNavigator key={myAccountStore.startRoute} initialRouteName={myAccountStore.startRoute} />
            </Animated.View>
          </GestureDetector>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default MyAccount;
